import { Suspense, memo, useEffect } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { EntraUserProvider, ToolsProvider, usePlatformStatus } from "@VinciDiane/core";
import { routes as smartImplantRoutes } from "@VinciDiane/service-smart-implant";
import { routes as comptageRoutes } from "@VinciDiane/service-comptage";
import RootLayout from "./components/layout/RootLayout";
import LoginPage from "./pages/login";
import PageMaintenance, { MAINTENANCE_BYPASS_KEY } from "./pages/errors/maintenance";

const queryClient = new QueryClient();

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <Navigate to="/smart-implant" replace /> },
      { path: 'maintenance', element: <PageMaintenance requireAdmin /> },
      ...smartImplantRoutes,
      ...comptageRoutes,
    ]
  }
], {
  future: {
    v7_relativeSplatPath: true,
  },
});

// Pas de props : memo() l'isole du polling de usePlatformStatus (toutes les 20s)
// dans AppContent, sinon tout l'arbre routé (jusqu'aux tables des pages) se
// re-rend à chaque poll.
const RoutedApp = memo(function RoutedApp() {
  return (
    <main>
      <div className="overflow-y-auto h-full w-full">
        <Suspense fallback={<div></div>}>
          <RouterProvider router={router} />
        </Suspense>
      </div>
    </main>
  );
});

// Statut plateforme géré directement par IEP_Back (getplatformstatus/setplatformstatus).
function AppContent() {
  const { status, isLoading } = usePlatformStatus();

  useEffect(() => {
    // isLoading : avant la 1ère résolution, `status` vaut le DEFAULT_STATUS
    // (enabled: false) du hook, pas encore la vraie valeur -- sans ce garde,
    // ce effect effaçait le bypass sur ce rendu transitoire, avant même que
    // le vrai statut (toujours enabled: true) n'arrive.
    // Ne couvre que le blocage en cours : une fois levé, le prochain
    // blocage doit à nouveau bloquer même les admins qui avaient passé outre.
    if (!isLoading && !status?.enabled) sessionStorage.removeItem(MAINTENANCE_BYPASS_KEY);
  }, [isLoading, status?.enabled]);

  if (status?.enabled && sessionStorage.getItem(MAINTENANCE_BYPASS_KEY) !== "1") {
    return <PageMaintenance />;
  }

  return <RoutedApp />;
}

function App() {
  useEffect(() => {
    if (import.meta.env.VITE_ENVIRONMENT === "production") return;

    // noindex hors production
    for (const name of ["robots", "googlebot"]) {
      const meta = document.createElement('meta');
      meta.name = name;
      meta.content = 'noindex';
      document.head.appendChild(meta);
    }
  }, []);

  return (<>
    <AuthenticatedTemplate>
      <QueryClientProvider client={queryClient}>
        <EntraUserProvider>
          <ToolsProvider>
            <AppContent />
          </ToolsProvider>
        </EntraUserProvider>
      </QueryClientProvider>
    </AuthenticatedTemplate>

    <UnauthenticatedTemplate>
      <LoginPage />
    </UnauthenticatedTemplate>
  </>
  );
}

export default App;
